import { useQuery } from 'react-query';
import { TActivityCounterparty } from '@tokenized/sdk-js-private';
import { useTokenizedApi } from '../useTokenizedApi';

/**
 * **`React hook`** Returns display information about a paymail handle
 *
 * The returned object has the following properties:
 *
 * - `isValid`: The definition of valid depends on the type of handle:
 *   - For Tokenized paymails: `valid` is `true` when the account exists.
 *   - For well-formed external paymails: `valid` is always `true`.
 *   - For well-formed BSV addresses: `valid` is always `true`.
 * - `address`: The BSV address for the contact, if known.
 * - `displayHandle`: Full-length paymail handle of the associated entity.
 * - `displayName`: Display name of the associated entity.
 * - `initials`: Up to two characters extracted from the name that can be used as an icon.
 * - `isAnonymous` If `true`, indicates this contact is an anonymous BSV address.
 * - `isInternal`: If `true`, indicates the handle is the paymail for a Tokenized account.
 *   If `false` then `handle` is an external paymail, and supports BSV transfers only.
 * - `isVerified`: Indicates whether the paymail has been verified.
 * - `avatarUrl`: Full URL to the entity's avatar image.
 * - `bio`: The bio text from the public profile associated with the handle.
 * - `website`: The website from the public profile associated with the handle.
 *
 * Will be refreshed automatically **every 10 minutes**.
 *
 * @param handle - The paymail handle to look up
 * @param counterparty - Supply the counterparty information from an activity event,
 *   and the full contact details will be returned. Note that the handle parameter
 *   will be ignored if you supply a counterparty object.
 *
 * @category Relationships
 */
export function useContact(
  handle: string | undefined,
  counterparty?: TActivityCounterparty,
) {
  const tokenizedApi = useTokenizedApi();
  const filteredCounterparty = counterparty
    ? {
        displayHandle: counterparty.displayHandle,
        displayName: counterparty.displayName,
        isAnonymous: counterparty.isAnonymous,
        isInternal: counterparty.isInternal,
        isVerified: counterparty.isVerified,
      }
    : null;
  return useQuery(
    tokenizedApi.relationships.handleQuery({
      handle,
      counterparty: filteredCounterparty,
    }),
  )?.data;
}
